import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { AccountControlTester, AccountControl } from 'src/components/JsonForm/controls/AccountsControl';
import { ArrayControlTester, ArrayControl } from 'src/components/JsonForm/controls/ArrayControl';
import { BooleanControl, BooleanControlTester } from 'src/components/JsonForm/controls/BooleanControl';
import { DateControlTester, DateControl } from 'src/components/JsonForm/controls/DateControl';
import { DateTimeControl, DateTimeControlTester } from 'src/components/JsonForm/controls/DateTimeControl';
import { EnumControlTester, EnumControl } from 'src/components/JsonForm/controls/EnumControl';
import { HiddenControl, HiddenControlTester } from 'src/components/JsonForm/controls/HiddenControl';
import { InputControlTester, InputControl } from 'src/components/JsonForm/controls/InputControl';
import { IntegerControl, IntegerControlTester } from 'src/components/JsonForm/controls/IntegerControl';
import { NumberControlTester, NumberControl } from 'src/components/JsonForm/controls/NumberControl';
import { OneOfEnumControlTester, OneOfEnumControl } from 'src/components/JsonForm/controls/OneOfEnumControl';
import { PriceControlTester, PriceControl } from 'src/components/JsonForm/controls/PriceControl';
import { QuantityControlTester, QuantityControl } from 'src/components/JsonForm/controls/QuantityControl';
import { SlotControlTester, SlotControlRenderer } from 'src/components/JsonForm/controls/SlotControl';
import type { SlotControlProps } from 'src/components/JsonForm/controls/SlotControl';
import { SymbolFinderControlTester, SymbolFinderControl } from 'src/components/JsonForm/controls/SymbolControl';
import { TimeControl, TimeControlTester } from 'src/components/JsonForm/controls/TimeControl';
import {
    GridCollapsibleLayoutRenderer,
    GridCollapsibleLayoutTester,
} from 'src/components/JsonForm/layouts/GridCollapsibleLayout';
import { GridLayoutTester, GridLayoutRenderer } from 'src/components/JsonForm/layouts/GridLayout';
import { GroupLayoutTester, GroupLayoutRenderer } from 'src/components/JsonForm/layouts/GroupLayout';
import { NoApplicableRenderer, NoApplicableRendererTester } from 'src/components/JsonForm/NoApplicableRenderer';

export const renderers = (slotsMapping?: SlotControlProps['slotsMapping']) => [
    { tester: SymbolFinderControlTester, renderer: SymbolFinderControl },
    { tester: AccountControlTester, renderer: AccountControl },
    { tester: QuantityControlTester, renderer: QuantityControl },
    { tester: PriceControlTester, renderer: PriceControl },
    { tester: InputControlTester, renderer: InputControl },
    { tester: IntegerControlTester, renderer: IntegerControl },
    { tester: NumberControlTester, renderer: NumberControl },
    { tester: EnumControlTester, renderer: EnumControl },
    { tester: OneOfEnumControlTester, renderer: OneOfEnumControl },
    { tester: BooleanControlTester, renderer: BooleanControl },
    { tester: DateControlTester, renderer: DateControl },
    { tester: DateTimeControlTester, renderer: DateTimeControl },
    { tester: TimeControlTester, renderer: TimeControl },
    { tester: ArrayControlTester, renderer: ArrayControl },
    { tester: HiddenControlTester, renderer: HiddenControl },
    {
        tester: SlotControlTester,
        renderer: (props: SlotControlProps) => <SlotControlRenderer {...props} slotsMapping={slotsMapping} />,
    },
    { tester: GroupLayoutTester, renderer: GroupLayoutRenderer },
    { tester: GridLayoutTester, renderer: GridLayoutRenderer },
    { tester: GridCollapsibleLayoutTester, renderer: GridCollapsibleLayoutRenderer },
    { tester: NoApplicableRendererTester, renderer: NoApplicableRenderer },
];

export const ignoredProps = ['statusWebhook', 'legs'];
export const hiddenProps = ['symbol', 'account', 'side', 'openClose', 'nonce'];
export const orderedProps = ['exDestination', 'route', 'type', 'quantity', 'price', 'stopPrice', 'duration'];
export const lightStyle = ['quantity', 'price', 'stopPrice'];
export const fakeRequired = ['exDestination', 'route', 'quantity', 'price', 'stopPrice', 'openClose']; // visually appear as required
export const uppercaseOptions = ['type', 'duration'];

export const defaultQuantityValue = 100;
export const defaultQuantityValueByInstrumentType = {
    equity: 100,
    option: 1,
};
export const defaultQuantityValueMultiLeg = 1;
export const defaultQuantityStep = 100;
export const defaultQuantityStepByInstrumentType = {
    equity: 100,
    option: 1,
};
export const defaultQuantityStepMultiLeg = 1;
export const defaultPriceStep = 0.01;
export const defaultPriceStepBelowADollar = 0.0001;
export const defaultPricePrecision = 2;
export const defaultPricePrecisionBelowADollar = 4;

export type InstrumentType = Instrument['type'];

export const pricePrecisionBelowADollarByInstrumentType: Record<InstrumentType, number> = {
    equity: 4,
    option: 2,
};

export const priceStepBelowADollarByInstrumentType: Record<InstrumentType, number> = {
    equity: 0.0001,
    option: 0.01,
};
